// The entry point owns browser-wide lifetimes only: language, history, the mounting root, session clock,
// the install hint's count of visits and offline registration. Pages own their data and connections, so
// leaving a route also leaves the resources that only made sense while it was visible.

import { seasonOn, type Season } from '@holydeck/contracts/church-season';
import { DEFAULT_LOOK } from '@holydeck/contracts/looks';
import { effect, signal } from '@preact/signals';
import { render } from 'preact';

import { startAccountCheck } from './account-switch.js';
import { App } from './app.js';
import { can, followAccountLocale, locale, session } from './app-state.js';
import { startAppWindow } from './app-window.js';
import { type ChurchProfile, churchProfile, loadChurchProfile, lookIsChosen } from './branding/church-profile.js';
import { forgetDevicePreference, writeDevicePreference } from './device-preferences.js';
import { draftsKept, SERMON_DRAFTS } from './drafts.js';
import {
  type ServiceWorkerContainerLike,
  registerServiceWorker,
} from './register-service-worker.js';
import { startInstallHint } from './install/install-hint.js';
import { startInputModality } from './input-modality.js';
import { startEditSocket } from './live-edit/edit-socket.js';
import { startMotion } from './motion.js';
import { startKeeping } from './offline-edits/keep.js';
import { startSync } from './offline-edits/sync.js';
import { checkEviction } from './offline-edits/waiting-cookie.js';
import { browserPushWorld } from './push/push-client.js';
import { browserEnvironment } from './push/push-switch-state.js';
import { startPushSync } from './push/push-sync.js';
import { guardUnload, matchRoute, startRouter } from './router.js';
import { boot, request } from './request.js';
import { startSessionTimer } from './session-timer.js';
import { forgetPending, keepConfirmedAccounts } from './sign-out.js';
import { startAppTheme } from './theme/app-theme.js';
import { startMotionChoice } from './theme/motion-preference.js';
import { startTheme } from './theme/theme-preference.js';
import { coverWithTabDrafts } from './unsaved-text.js';
import { activateOnOutputLoad, isUpdateContainer, startUpdateChecks, watchForUpdates } from './update-ready.js';

/** The season a profile asks for today, or none: `auto` follows this device's own calendar date. */
const seasonOf = (profile: ChurchProfile | undefined, today: Date): Season | undefined => {
  const choice = profile?.season;
  if (choice === undefined || choice === 'off') {
    return undefined;
  }

  if (choice !== 'auto') {
    return choice;
  }

  const season = seasonOn({ year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() });
  return season === 'ordinary' ? undefined : season;
};

/** Starts the mounted application and returns the browser-wide lifetimes its caller can stop. */
export function start(win: Window): () => void {
  const root = win.document.getElementById('app');
  if (root === null) {
    return () => undefined;
  }

  const stopAppWindow = startAppWindow(win, win.document.documentElement);
  const stopTheme = startTheme(win.document);
  const stopMotionChoice = startMotionChoice(win.document);
  const stopInputModality = startInputModality(win.document);
  const stopMotion = startMotion(win.document);
  const stopInstallHint = startInstallHint(win);
  const stopRouter = startRouter(win);
  // Sermon form edits the server does not hold yet wait in this tab's drafts, which closing the tab ends, so
  // closing it asks while any is kept, whether or not its editor is on screen. A signed-in account that may not
  // edit content can restore none of them; one whose session lapsed or is still loading may yet. A reload keeps
  // them, so this hold never holds an update; an open sermon form holds through its own holds.
  const sermonDraftsHeld = (): boolean => {
    if (!draftsKept(SERMON_DRAFTS)) {
      return false;
    }

    return session.value === null || session.value === undefined || can('content.edit');
  };

  const releaseDrafts = guardUnload(sermonDraftsHeld, { kept: true, userClears: true, holdsUpdate: false });
  const uncoverDrafts = coverWithTabDrafts(sermonDraftsHeld, SERMON_DRAFTS);
  const stopLocale = effect(() => {
    win.document.documentElement.lang = locale.value;
  });
  // A device can stay open for days, so an automatic season follows the date at each local midnight.
  const today = signal(new Date());
  let midnight: ReturnType<typeof setTimeout> | undefined;
  const followMidnight = (): void => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    midnight = setTimeout(() => {
      today.value = new Date();
      followMidnight();
    }, next.getTime() - now.getTime());
  };

  followMidnight();
  const stopAuroraTint = effect(() => {
    const profile = churchProfile.value;
    const season = seasonOf(profile, today.value);
    const tint = season === undefined ? profile?.accent : `var(--season-${season})`;
    if (tint === undefined) {
      win.document.documentElement.style.removeProperty('--aurora-tint');
      return;
    }

    win.document.documentElement.style.setProperty('--aurora-tint', tint);
  });
  const stopChurchLook = effect(() => {
    const profile = churchProfile.value;
    if (profile === undefined) {
      return;
    }

    const look = profile.look ?? DEFAULT_LOOK;
    win.document.documentElement.dataset.look = look;
    if (lookIsChosen(profile)) {
      writeDevicePreference('look', look, win.localStorage);
    } else {
      forgetDevicePreference('look', win.localStorage);
    }
  });
  const stopAppTheme = startAppTheme(win.document.documentElement, win.localStorage);
  const stopAccountLocale = followAccountLocale(win.localStorage);
  root.replaceChildren();
  render(<App />, root);
  void boot();
  const stopPushSync = startPushSync({
    world: browserPushWorld(win, request),
    environment: browserEnvironment(win),
    outcomes: 'serviceWorker' in win.navigator ? win.navigator.serviceWorker : undefined,
  });
  void loadChurchProfile();
  const stopSessionTimer = startSessionTimer();
  const stopAccountCheck = startAccountCheck();
  const stopEditing = startEditSocket();
  const stopKeepingConfirmed = keepConfirmedAccounts();
  void forgetPending();
  const stopKeeping = startKeeping();
  void checkEviction();
  const stopSync = startSync();
  return (): void => {
    stopSync();
    stopKeeping();
    stopKeepingConfirmed();
    stopPushSync();
    stopSessionTimer();
    stopAccountCheck();
    stopEditing();
    stopLocale();
    clearTimeout(midnight);
    stopAuroraTint();
    stopChurchLook();
    stopAppTheme();
    stopTheme();
    stopMotionChoice();
    stopInputModality();
    stopMotion();
    stopAppWindow();
    stopAccountLocale();
    releaseDrafts();
    uncoverDrafts();
    stopRouter();
    stopInstallHint();
    render(null, root);
  };
}

const browser: {
  readonly document?: Document;
  readonly location?: Pick<Location, 'pathname' | 'search'>;
  readonly navigator?: { readonly serviceWorker?: ServiceWorkerContainerLike };
  readonly window?: Window;
} = globalThis;

const workers = browser.navigator?.serviceWorker;

let stopStartedImpl: () => void = () => undefined;

/** Stops the application this module started; nothing when it started none. */
export const stopStarted = (): void => {
  stopStartedImpl();
  stopStartedImpl = () => undefined;
};

/** Draws the application and registers the worker, as every page load does. */
const begin = (): void => {
  if (browser.document !== undefined && browser.window !== undefined) {
    stopStartedImpl = start(browser.window);
  }

  void registerServiceWorker(
    workers,
    '/service-worker.js',
    (error) => {
      console.warn('HolyDeck will run online only: the service worker was refused', error);
    },
    (registration) => {
      if (isUpdateContainer(workers)) {
        watchForUpdates(registration, workers);
        startUpdateChecks(registration);
      }
    },
  );
};

// An output window takes a waiting build as it loads, before its first deck and before anyone can press
// fullscreen, and reloads once into it; while it stays open it never reloads for an update.
const location = browser.location;
const outputLoad = location !== undefined && matchRoute(`${location.pathname}${location.search}`).name === 'output';
if (outputLoad) {
  void activateOnOutputLoad(isUpdateContainer(workers) ? workers : undefined, begin)
    .catch(() => false)
    .then((reloading) => {
      if (!reloading) {
        begin();
      }
    });
} else {
  begin();
}
